import { useEffect, useState } from 'react';
import type { AnimalSummary, Profile } from '../../shared/types/api';
import { AnimalCard } from '../components/AnimalCard';
import { CompassMark } from '../components/CompassMark';
import { ErrorNotice } from '../components/ErrorNotice';
import { ParentPanel } from '../components/ParentPanel';
import { TrailCompassArtwork } from '../components/TrailCompassArtwork';
import { api } from '../lib/api';

interface HomePageProps {
  profile: Profile;
  recoveryCode: string | null;
  onRecover: (code: string) => Promise<void>;
  onOpenAnimal: (animalId: string) => void;
}

export function HomePage({ profile, recoveryCode, onRecover, onOpenAnimal }: HomePageProps) {
  const [animals, setAnimals] = useState<AnimalSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [unlockingAnimalId, setUnlockingAnimalId] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    api
      .listAnimals(profile.id)
      .then((result) => {
        if (active) setAnimals(result);
      })
      .catch((reason: unknown) => {
        if (active)
          setError(reason instanceof Error ? reason.message : 'The animals could not load.');
      });
    return () => {
      active = false;
    };
  }, [profile.id]);

  async function unlockAnimal(animalId: string) {
    setUnlockingAnimalId(animalId);
    setError(null);
    try {
      const unlockedAnimal = await api.unlockAnimal(profile.id, animalId);
      setAnimals((currentAnimals) =>
        currentAnimals.map((animal) => (animal.id === unlockedAnimal.id ? unlockedAnimal : animal)),
      );
      onOpenAnimal(unlockedAnimal.id);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'That trail could not be unlocked.');
    } finally {
      setUnlockingAnimalId(null);
    }
  }

  return (
    <div className="app-shell home-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Wild Trails home">
          <CompassMark />
          <span>Wild Trails</span>
        </a>
        <ParentPanel recoveryCode={recoveryCode} onRecover={onRecover} />
      </header>

      <main>
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Your animal team</p>
            <h1>Where will their trails lead today?</h1>
            <p className="hero-intro">
              Pick an animal friend, open the map, and follow every new adventure.
            </p>
          </div>
          <TrailCompassArtwork />
        </section>

        {error && <ErrorNotice message={error} />}
        <section className="animal-grid" aria-label="Animal friends">
          {animals.map((animal) => (
            <AnimalCard
              key={animal.id}
              animal={animal}
              unlocking={unlockingAnimalId === animal.id}
              onSelect={() => onOpenAnimal(animal.id)}
              onUnlock={() => void unlockAnimal(animal.id)}
            />
          ))}
        </section>
        <p className="family-footnote">Trail saved for {profile.displayName}</p>
      </main>
    </div>
  );
}
